import { useMemo } from 'react';
import { SpotSelectBox, SpotSelectOption, SpotCheckboxGroup, SpotCheckbox, SpotHorizontalFields, SpotButton, SpotModal } from '@enterprise/spot';
import { Form, Field, FormRenderProps } from '@enterprise/common';
import { Trans } from 'react-i18next';
import i18n from 'i18next';
import { ConstantsStore } from '../../../../../store/constants.store';
import { DatasourceFullSiteInfo } from '../../../../../core/models/datasource/sites';
import { ProviderDetails } from '../../../../../core/models';

interface VariantsControlsProps {
    constants: ConstantsStore;
    existingVariantsIds: string[];
    masterSiteId: string;
    selectedVariant?: string;
    disabled?: boolean;
    onVariantChanged?: (siteId: string) => void;
    onVariantAdded?: (name, copyFrom?) => void;
}

interface SortedSites {
    sitesWithVariant: DatasourceFullSiteInfo[];
    sitesWithoutVariant: DatasourceFullSiteInfo[];
}

interface VariantData {
    copyFrom: string;
    variantProviders: ProviderDetails[];
}

export const VariantsControl = function VariantsControlComponent(props: VariantsControlsProps) {
    const { constants, disabled, selectedVariant, existingVariantsIds, masterSiteId } = props;
    const data = useMemo(() => ({ copyFrom: 'master', variantProviders: [] } as VariantData), []);

    const { sitesWithVariant, sitesWithoutVariant }: SortedSites = useMemo(() => {
        const sortedSites: SortedSites = {
            sitesWithVariant: [],
            sitesWithoutVariant: [],
        };

        return constants.applications.reduce((agg, site) => {
            if (!site.isActive || site.id === masterSiteId) {
                return agg;
            }

            const siteIdWithVariant = existingVariantsIds.find((variantSiteId) => {
                return variantSiteId === site.id;
            });

            if (siteIdWithVariant) {
                agg.sitesWithVariant.push(site);
            } else {
                agg.sitesWithoutVariant.push(site);
            }
            return agg;
        }, sortedSites);
    }, [constants.applications, existingVariantsIds]);

    const onCreateClicked = (handleSubmit: FormRenderProps<VariantData>['handleSubmit'], close: () => void) => {
        return () => {
            handleSubmit();
            setTimeout(close, 500);
        };
    };

    const onVariantSelected = (event) => {
        const variantSiteId = event.target.value;
        const { onVariantChanged } = props;
        if (variantSiteId && onVariantChanged) {
            onVariantChanged(variantSiteId);
        }
    };

    const onVariantsAdded = (data: VariantData) => {
        data.variantProviders.forEach((site) => {
            props.onVariantAdded?.(site, data.copyFrom);
        });
    };

    const renderModalController = ({ open }) => {
        return (
            <a onClick={open}>
                <Trans i18nKey="variantsControls:addVariant">Add a variant</Trans>
            </a>
        );
    };

    return (
        <div>
            <div>
                <div style={{ display: 'inline-block', width: '50%' }}>
                    <label className="spot-form__label">
                        <Trans i18nKey="variantsControls:variant">Variant:&nbsp;</Trans>
                    </label>
                    {sitesWithVariant.length === 0 && (
                        <label className="spot-form__label--disabled">
                            <Trans i18nKey="variantsControls:noVariantsAvailable">No variants available</Trans>
                        </label>
                    )}
                    {sitesWithVariant.length > 0 && (
                        <SpotSelectBox
                            onChange={onVariantSelected}
                            disabled={disabled}
                            value={selectedVariant}
                            style={{ display: 'inline-block' }}
                            placeholder={i18n.t('variantsControls:selectVariantToModify', 'Select a Variant to Modify')}
                        >
                            {sitesWithVariant.map(({ id, name }) => (
                                <SpotSelectOption key={id} value={id}>
                                    {name}
                                </SpotSelectOption>
                            ))}
                        </SpotSelectBox>
                    )}
                </div>
                <div style={{ display: 'inline-block', width: '50%', textAlign: 'right' }}>
                    <SpotModal title="Add New Variant" controller={renderModalController}>
                        {({ close }) => (
                            <div>
                                <Form data={data} onSubmit={onVariantsAdded}>
                                    {({ handleSubmit }: FormRenderProps<VariantData>) => (
                                        <div>
                                            <div>
                                                <SpotHorizontalFields>
                                                    <div>
                                                        <label className="spot-form__label">
                                                            <Trans i18nKey="variantsControls:selectClinicsForVariant">
                                                                Select Clinics for the Variant
                                                            </Trans>
                                                        </label>

                                                        {sitesWithoutVariant.length === 0 && (
                                                            <label className="spot-form__label--disabled">
                                                                <Trans i18nKey="variantsControls:noClinicsForVariants">
                                                                    No clinics available to add variant. Check My Organization to ensure the desired
                                                                    clinic is online
                                                                </Trans>
                                                            </label>
                                                        )}
                                                        {sitesWithoutVariant.length > 0 && (
                                                            <SpotCheckboxGroup isHorizontal={true}>
                                                                {sitesWithoutVariant.map((app) => (
                                                                    <Field key={app.id} name="variantProviders" type="checkbox" value={app.provider}>
                                                                        {({ input }) => (
                                                                            <SpotCheckbox {...input} value={input.value?.name}>
                                                                                {app.name}
                                                                            </SpotCheckbox>
                                                                        )}
                                                                    </Field>
                                                                ))}
                                                            </SpotCheckboxGroup>
                                                        )}
                                                    </div>
                                                </SpotHorizontalFields>
                                            </div>
                                            <div>
                                                <SpotHorizontalFields>
                                                    <div>
                                                        <label className="spot-form__label">
                                                            <Trans i18nKey="variantsControls:copyFrom">Copy from</Trans>
                                                        </label>
                                                        <Field name="copyFrom">
                                                            {({ input, meta }) => (
                                                                <SpotSelectBox {...input}>
                                                                    <SpotSelectOption value="master">Master</SpotSelectOption>
                                                                    {sitesWithVariant.map((app) => (
                                                                        <SpotSelectOption key={app.id} value={app.id}>
                                                                            {app.name}
                                                                        </SpotSelectOption>
                                                                    ))}
                                                                </SpotSelectBox>
                                                            )}
                                                        </Field>
                                                    </div>
                                                </SpotHorizontalFields>
                                            </div>
                                            <div>
                                                <span style={{ width: '50%', display: 'inline-block' }}>
                                                    <a onClick={close}>Cancel</a>
                                                </span>
                                                <span
                                                    style={{
                                                        width: '50%',
                                                        display: 'inline-block',
                                                        textAlign: 'right',
                                                    }}
                                                >
                                                    <SpotButton
                                                        data-automation-id={'variant-controls-create-button'}
                                                        onClick={onCreateClicked(handleSubmit, close)}
                                                        disabled={!sitesWithoutVariant?.length}
                                                    >
                                                        Create
                                                    </SpotButton>
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </Form>
                            </div>
                        )}
                    </SpotModal>
                </div>
            </div>
        </div>
    );
};
